import React, { type ReactNode, useState } from "react"

import { TooltipContent, TooltipTrigger } from "@radix-ui/react-tooltip"
import {
  BadgeAlert,
  Building,
  ChevronDown,
  ChevronRight,
  CreditCard,
  FileText,
  type LucideIcon,
  Mail,
  MapPin,
  User,
  Users,
  Verified,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { useAuth } from "@/contexts/AuthContext"
import type { ApplicationDetails } from "@/hooks/useApplicationDetails"

import { PermissionGuard } from "../common/PermissionGuard"
import { Tooltip } from "../ui/tooltip"
import PersonRecordSelector from "./PersonRecordSelector"

interface ApplicationInfoProps {
  application: ApplicationDetails
  onEdit?: () => void
}

const ApplicationInfo = ({ application, onEdit }: ApplicationInfoProps) => {
  const { user, claims } = useAuth()
  const isDSP = claims.user_role === "DSP"
  const applicant = application.applicant

  const dvlaRecord = application.driver_licence_records?.[0]
  const isDobVerifiedByDvla =
    dvlaRecord && applicant?.date_of_birth && dvlaRecord.date_of_birth
      ? new Date(applicant.date_of_birth).toISOString().split("T")[0] ===
        new Date(dvlaRecord.date_of_birth).toISOString().split("T")[0]
      : false

  const calculateAge = (dob?: string | null) => {
    if (!dob) return null
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const age = calculateAge(applicant?.date_of_birth)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    personal: true,
    contact: true,
    banking: false,
    stripe: false,
    tax: false,
    nextOfKin: false,
    address: true,
    work: true,
  })

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const CollapsibleSection = ({
    id,
    title,
    icon: Icon,
    children,
    sensitive = false,
  }: {
    id: string
    title: string
    icon: LucideIcon
    children: ReactNode
    sensitive?: boolean
  }) => (
    <Card className={`shadow-sm ${sensitive ? "border-amber-200 bg-amber-50/30" : ""}`}>
      <Collapsible open={openSections[id]} onOpenChange={() => toggleSection(id)}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <Icon className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg font-semibold">{title}</CardTitle>
                {sensitive && (
                  <Badge variant="outline" className="text-xs">
                    Sensitive
                  </Badge>
                )}
              </div>
              {openSections[id] ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">{children}</CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )

  const InfoField = ({
    label,
    value,
    className = "",
  }: {
    label: string
    value: string | undefined | null
    className?: string
  }) => (
    <div className={className}>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-medium break-words">{value || "Not provided"}</p>
    </div>
  )

  const VerifiableInfoField = ({
    label,
    value,
    className = "",
    verified,
    verificationSource = "API",
  }: {
    label: string
    value: string | undefined | null
    className?: string
    verified?: boolean
    verificationSource?: string
  }) => (
    <div className={className}>
      <p className="text-sm text-muted-foreground">{label}</p>
      {value ? (
        <div className="flex items-center font-medium break-words">
          {value}{" "}
          {verified !== undefined && (
            <Tooltip>
              {verified ? (
                <TooltipTrigger className="cursor-default ml-2 rounded-full bg-green-100">
                  <Verified className="h-4 w-4 text-green-500" />
                </TooltipTrigger>
              ) : (
                <TooltipTrigger className="cursor-default ml-2 rounded-full bg-amber-100">
                  <BadgeAlert className="h-4 w-4 text-amber-500" />
                </TooltipTrigger>
              )}
              <TooltipContent sideOffset={5} className="bg-white rounded-md shadow-sm p-2 border">
                <p className="text-sm">
                  {verified
                    ? `Verified via ${verificationSource}`
                    : `Not verified via ${verificationSource}`}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      ) : (
        <p className="font-medium break-words">Not provided</p>
      )}
    </div>
  )

  const StatusBadge = ({ status }: { status?: string | null }) => {
    if (!status)
      return (
        <Badge variant="outline" className="pointer-events-none">
          Not provided
        </Badge>
      )

    const statusColors = {
      Verified: "bg-green-100 text-green-800 border-green-200",
      Pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Rejected: "bg-red-100 text-red-800 border-red-200",
      "Not Submitted":
        "bg-palette-jungleGreen100 text-palette-jungleGreen800 border-palette-jungleGreen200",
    }

    return (
      <Badge
        className={
          statusColors[status as keyof typeof statusColors] ||
          "bg-palette-jungleGreen100 text-palette-jungleGreen800 pointer-events-none"
        }
      >
        {status}
      </Badge>
    )
  }

  const getWorkDays = () => {
    if (!applicant?.availability_days) return "Not specified"
    const selectedDays = applicant.availability_days.map(
      (day) => day.charAt(0).toUpperCase() + day.slice(1),
    )
    return selectedDays.length > 0 ? selectedDays.join(", ") : "None selected"
  }

  return (
    <div className="space-y-4">
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-24 w-24 flex items-center justify-center">
              {application.headshot ? (
                <img
                  src={application.headshot}
                  alt={application.name}
                  className="h-24 w-24 rounded-md"
                />
              ) : (
                <User className="h-24 w-24 text-primary" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-secondary">{application.name}</h1>
              <p className="text-muted-foreground">{application.job?.title}</p>
              <p className="text-sm text-muted-foreground">
                Applied:{" "}
                {application.created_at
                  ? new Date(application.created_at).toLocaleDateString()
                  : "Not available"}
              </p>
            </div>
          </div>
          <PermissionGuard
            resource="applications"
            action="update"
            scopes={["own", "any"]}
            fallback={null}
          >
            {onEdit && (
              <Button onClick={onEdit} variant="outline">
                Edit Details
              </Button>
            )}
          </PermissionGuard>
        </div>
      </div>

      {application.application_id && user?.role !== "DSP" && (
        <PersonRecordSelector currentPersonId={application.person?.id} />
      )}

      <CollapsibleSection id="personal" title="Personal Details" icon={User}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoField label="First Name" value={applicant?.first_name} />
          <InfoField label="Last Name" value={applicant?.last_name} />
          <VerifiableInfoField
            label="Date of Birth"
            value={
              applicant?.date_of_birth
                ? `${applicant.date_of_birth}${age ? ` (${age} years)` : ""}`
                : undefined
            }
            verified={isDobVerifiedByDvla}
            verificationSource="DVLA"
          />
          <VerifiableInfoField
            label="Driving Licence Number"
            value={applicant?.driving_licence_number}
            verified={applicant?.driving_licence_number_verified_via_api}
            verificationSource="DVLA API"
          />
          <VerifiableInfoField
            label="RTW Share Code"
            value={applicant?.share_code_rtw}
            verified={applicant?.share_code_rtw_verified_via_api}
            verificationSource="RTW API"
          />
          <InfoField label="Submitted Date" value={application.created_at} />
          <InfoField label="CPC Number" value={applicant?.cpc_number} />
          <InfoField label="Tachograph Card Number" value={applicant?.tachograph_card_number} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection id="contact" title="Contact Information" icon={Mail}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoField label="Email" value={applicant?.email} />
          <InfoField label="Phone" value={applicant?.phone_number} />
          <InfoField label="Location" value={applicant?.city} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection id="work" title="Work Preferences" icon={Building}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoField label="Job Posting" value={application.job?.title} />
          <InfoField label="Days per Week" value={applicant?.availability_days_count} />
          <InfoField label="Working Days" value={getWorkDays()} />
          <div>
            <p className="text-sm text-muted-foreground">Has Own Vehicle</p>
            <Badge variant={applicant?.has_electric_van ? "default" : "secondary"}>
              {applicant?.has_electric_van ? "Yes" : "No"}
            </Badge>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Requires Rental Van</p>
            <Badge variant={applicant?.needs_rental_van ? "default" : "secondary"}>
              {applicant?.needs_rental_van ? "Yes" : "No"}
            </Badge>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection id="address" title="Address Information" icon={MapPin}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoField label="Address Line 1" value={applicant?.address_line_1} />
          <InfoField label="Address Line 2" value={applicant?.address_line_2} />
          <InfoField label="City" value={applicant?.city} />
          <InfoField label="Postcode" value={applicant?.postcode} />
        </div>
      </CollapsibleSection>

      {!isDSP && (
        <CollapsibleSection id="banking" title="Bank Details" icon={Building} sensitive>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoField label="Bank Name" value={applicant?.bank_name} />
            <InfoField label="Account Name" value={applicant?.account_holder_name} />
            <InfoField label="Account Number" value={applicant?.account_number} />
            <InfoField label="Sort Code" value={applicant?.sort_code} />
            <div>
              <p className="text-sm text-muted-foreground">Bank Proof Status</p>
              <StatusBadge status={application.bank_proof_status} />
            </div>
          </div>
        </CollapsibleSection>
      )}

      {!isDSP && (
        <CollapsibleSection id="stripe" title="Stripe Payment Details" icon={CreditCard} sensitive>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoField label="Stripe Customer ID" value={application.stripe_customer_id} />
            <InfoField label="Payment Method ID" value={application.stripe_payment_method_id} />
            <InfoField
              label="Payment Added"
              value={
                application.payment_added_date
                  ? new Date(application.payment_added_date).toLocaleString()
                  : undefined
              }
            />
          </div>
        </CollapsibleSection>
      )}

      {!isDSP && (
        <CollapsibleSection id="tax" title="Tax & Company Details" icon={FileText} sensitive>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InfoField label="National Insurance Number" value={applicant?.ni_number} />
            <InfoField label="UTR Number" value={applicant?.utr_number} />
            <InfoField label="Company Type" value={applicant?.company_type} />
            <InfoField label="Company Number" value={applicant?.company_number} />
            <InfoField label="VAT Number" value={applicant?.vat_number} />
          </div>
        </CollapsibleSection>
      )}

      <CollapsibleSection id="nextOfKin" title="Next of Kin" icon={Users}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoField label="Name" value={applicant?.next_of_kin_name} />
          <InfoField label="Phone Number" value={applicant?.next_of_kin_phone} />
          <InfoField label="Relationship" value={applicant?.next_of_kin_relation} />
        </div>
      </CollapsibleSection>
    </div>
  )
}

export default ApplicationInfo
